import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { readSession } from '../_helpers/session.js';
import config from '../config.js';

/*
 * The signed-in listener's Photosensitivity mode (ENG-836) and whether they
 * have acknowledged the photosensitivity warning, from the account (ENG-835):
 *
 * - `GET /me` carries `photosensitivityMode` (bool) and
 *   `photosensitivityWarningAckAt` (RFC 3339 string, or null).
 * - `PATCH /me/preferences {photosensitivityMode}` answers 200
 *   `{photosensitivityMode}`; idempotent, last write wins.
 * - `POST /me/photosensitivity-warning/ack` answers 204, every time.
 *
 * This provider is the only holder of the setting on the page: the profile
 * toggle and the stream-page switch both read and write it here, so a change
 * in one shows in the other at once and a running FX stage follows it live
 * (`useReduceEffects`). Other devices pick a change up on their next GET /me.
 *
 * Like LocalePreferenceSync it sits inside the Router and re-checks the
 * session on every route change (every sign-in and sign-out ends in one): GET
 * /me is fetched on every page load with a session and again after signing
 * in. Without a session it holds no account state and calls nothing. The page
 * never waits on it.
 */

export const PHOTOSENSITIVITY_STATUS = Object.freeze({
  NO_SESSION: 'no-session',
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
});

const { NO_SESSION, LOADING, READY, ERROR } = PHOTOSENSITIVITY_STATUS;

/**
 * sessionStorage key holding the id of the account whose warning
 * acknowledgment failed to reach the server: the warning stays closed for the
 * rest of this browser session, and the next session asks again because the
 * server still reports null.
 */
export const WARNING_DISMISSED_KEY = 'songshake:photosensitivity-warning-dismissed';

const meEndpoint = () => `${config.apiBaseUrl}/me`;
const preferencesEndpoint = () => `${config.apiBaseUrl}/me/preferences`;
const ackEndpoint = () => `${config.apiBaseUrl}/me/photosensitivity-warning/ack`;

const readDismissed = () => {
  try {
    return sessionStorage.getItem(WARNING_DISMISSED_KEY);
  } catch (_e) {
    return null;
  }
};

const writeDismissed = (accountId) => {
  try {
    sessionStorage.setItem(WARNING_DISMISSED_KEY, String(accountId ?? ''));
  } catch (_e) {
    // Blocked storage: the warning still stays closed for this page.
  }
};

const noop = () => {};

/** The value with no provider mounted, and with no session: no account layer at all. */
const SIGNED_OUT = Object.freeze({
  status: NO_SESSION,
  photosensitivityMode: false,
  warningAckAt: null,
  warningOpen: false,
  saveStatus: 'idle',
  saveError: null,
  setPhotosensitivityMode: noop,
  retrySave: noop,
  acknowledgeWarning: noop,
});

export const AccountFxPreferencesContext = createContext(SIGNED_OUT);

/**
 * `{ status, photosensitivityMode, warningAckAt, warningOpen, saveStatus,
 * saveError, setPhotosensitivityMode(on), retrySave(), acknowledgeWarning() }`
 *
 * - `status`: 'no-session' | 'loading' | 'ready' | 'error'.
 * - `warningOpen`: the first-login warning should be showing (ready, never
 *   acknowledged, and not dismissed earlier in this browser session).
 * - `saveStatus`: 'idle' | 'saving' | 'saved' | 'error', for the latest change.
 * - `saveError`: the failed save's error, or null.
 */
export const useAccountFxPreferences = () => useContext(AccountFxPreferencesContext);

export function AccountFxPreferencesProvider({ children }) {
  const location = useLocation();
  const [hasSession, setHasSession] = useState(readSession);
  useEffect(() => {
    setHasSession(readSession());
  }, [location.pathname]);

  const fetchWrapperRef = useRef(useFetchWrapper());
  const [status, setStatus] = useState(hasSession ? LOADING : NO_SESSION);
  const [accountId, setAccountId] = useState(null);
  const [photosensitivityMode, setMode] = useState(false);
  const [warningAckAt, setWarningAckAt] = useState(null);
  const [dismissedThisSession, setDismissedThisSession] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [saveError, setSaveError] = useState(null);

  // The last value the server confirmed, to revert to when a save fails.
  const confirmedRef = useRef(false);
  // The value of the latest change, for Retry.
  const lastAttemptRef = useRef(null);
  // Only the latest save may report its outcome.
  const saveSeqRef = useRef(0);
  const statusRef = useRef(status);
  statusRef.current = status;
  const accountIdRef = useRef(accountId);
  accountIdRef.current = accountId;
  const ackSentRef = useRef(false);

  useEffect(() => {
    saveSeqRef.current += 1;
    setSaveStatus('idle');
    setSaveError(null);
    ackSentRef.current = false;
    if (!hasSession) {
      setStatus(NO_SESSION);
      setAccountId(null);
      setMode(false);
      setWarningAckAt(null);
      setDismissedThisSession(false);
      confirmedRef.current = false;
      return undefined;
    }
    let cancelled = false;
    setStatus(LOADING);
    fetchWrapperRef.current
      .get(meEndpoint())
      .then((me) => {
        if (cancelled) return;
        const mode = me?.photosensitivityMode === true;
        confirmedRef.current = mode;
        setMode(mode);
        // Only an explicit null shows the warning: a backend without the
        // field (undefined) must not open it on every page.
        setWarningAckAt(me && 'photosensitivityWarningAckAt' in me ? me.photosensitivityWarningAckAt : '');
        setAccountId(me?.id ?? null);
        const dismissed = readDismissed();
        setDismissedThisSession(dismissed != null && dismissed === String(me?.id ?? ''));
        setStatus(READY);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load account preferences:', err);
        setStatus(ERROR);
      });
    return () => {
      cancelled = true;
    };
  }, [hasSession]);

  const setPhotosensitivityMode = useCallback((on) => {
    if (statusRef.current !== READY) return;
    const value = Boolean(on);
    const seq = ++saveSeqRef.current;
    lastAttemptRef.current = value;
    // Optimistic: the page (and a running stage) follows at once.
    setMode(value);
    setSaveStatus('saving');
    setSaveError(null);
    fetchWrapperRef.current
      .patch(preferencesEndpoint(), { photosensitivityMode: value })
      .then((body) => {
        const stored = typeof body?.photosensitivityMode === 'boolean' ? body.photosensitivityMode : value;
        confirmedRef.current = stored;
        // A slower, earlier answer must not overwrite a newer choice.
        if (seq !== saveSeqRef.current) return;
        setMode(stored);
        setSaveStatus('saved');
      })
      .catch((err) => {
        if (seq !== saveSeqRef.current) return;
        console.error('Failed to save Photosensitivity mode:', err);
        setMode(confirmedRef.current);
        setSaveStatus('error');
        setSaveError(err ?? { status: 0 });
      });
  }, []);

  const retrySave = useCallback(() => {
    if (lastAttemptRef.current == null) return;
    setPhotosensitivityMode(lastAttemptRef.current);
  }, [setPhotosensitivityMode]);

  const acknowledgeWarning = useCallback(() => {
    if (statusRef.current !== READY || ackSentRef.current) return;
    ackSentRef.current = true;
    setWarningAckAt(new Date().toISOString());
    const id = accountIdRef.current;
    const failed = (err) => {
      console.error('Failed to record the photosensitivity warning acknowledgment:', err);
      // Closed for the rest of this session; the next one asks again.
      writeDismissed(id);
      setDismissedThisSession(true);
    };
    try {
      Promise.resolve(fetchWrapperRef.current.post(ackEndpoint())).catch(failed);
    } catch (err) {
      failed(err);
    }
  }, []);

  const warningOpen = status === READY && warningAckAt === null && !dismissedThisSession;

  const value = useMemo(
    () => (status === NO_SESSION
      ? SIGNED_OUT
      : {
        status,
        photosensitivityMode,
        warningAckAt,
        warningOpen,
        saveStatus,
        saveError,
        setPhotosensitivityMode,
        retrySave,
        acknowledgeWarning,
      }),
    [status, photosensitivityMode, warningAckAt, warningOpen, saveStatus, saveError, setPhotosensitivityMode, retrySave, acknowledgeWarning]
  );

  return <AccountFxPreferencesContext.Provider value={value}>{children}</AccountFxPreferencesContext.Provider>;
}

export default AccountFxPreferencesProvider;
